// Env-gated taxonomy client. Mirrors editorialQuestion.ts pattern.
//
//   - REACT_APP_EDITORIAL_QUESTION_CLIENT='mock' → taxonomyMockClient
//   - otherwise (default) → taxonomyHenryClient (api_v2/item/topic_type/get_list)
//
// Henry's finalized `topic_type/get_list` returns a flat adjacency list of
// nodes (`FlatTopicRow`, linked by `parent_id`). The panel's editor + cascade
// UI expect the hierarchical `TaxonomySubject[]` tree (subject → area → unit →
// category). taxonomyHenryClient bridges the two via `adjacencyToTree`, which
// links nodes by `parent_id` and derives the level from depth-from-root.

import * as mainResourceClient from './main_resource';
import { FlatTopicRow } from './main_resource.types';
import { normalizeSubjectName } from './subjectNames';
import {
  TaxonomyLevel,
  TaxonomyNode,
  TaxonomySubject,
  taxonomyClient as taxonomyMockClient,
  taxonomyToObjectiveCascade
} from './taxonomy.mock';

export type {
  TaxonomyLevel,
  TaxonomyNode,
  TaxonomySubject,
  ObjectiveCascade
} from './taxonomy.mock';
export {
  countTaxonomyLevels,
  createTaxonomyNode,
  findTaxonomyNode,
  getNextTaxonomyLevel,
  removeTaxonomyNode,
  taxonomyToObjectiveCascade,
  updateTaxonomyNode,
  upsertTaxonomyNode
} from './taxonomy.mock';
export { getSubjectDisplayName, normalizeSubjectName } from './subjectNames';

function makeNode(
  level: TaxonomyLevel,
  name: string,
  code: string,
  description = '',
  children: TaxonomyNode[] = [],
  parentId = ''
): TaxonomyNode {
  // Path-qualify the id so a domain/unit name reused across subjects (e.g. "數"
  // under more than one subject) cannot collide. A bare `${level}:${name}` made
  // duplicate React keys + mis-toggled tree expansion. Category leaves are keyed
  // on topic_type_id (already unique) but stay path-qualified for consistency.
  const localId = `${level}:${code || name}`;
  return {
    id: parentId ? `${parentId}>${localId}` : localId,
    level,
    name,
    code,
    description,
    children
  };
}

// Map a TaxonomyNode tree depth to the panel's level vocabulary.
const LEVEL_BY_DEPTH: TaxonomyLevel[] = ['subject', 'area', 'unit', 'category'];
const LEVEL_NUM: Record<TaxonomyLevel, number> = {
  subject: 1,
  area: 2,
  unit: 3,
  category: 4
};

const isRootParent = (p: string | number | null | undefined): boolean =>
  p == null || p === '' || p === 0 || p === '0';

const sortFlatRows = (a: FlatTopicRow, b: FlatTopicRow): number => {
  const sa = Number(a.sort ?? 0);
  const sb = Number(b.sort ?? 0);
  if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb;
  return Number(a.id) - Number(b.id);
};

// Build the subject → area → unit → category tree from Henry's finalized flat
// adjacency list (`topic_type/get_list`). Nodes are linked by `parent_id`
// (null/0/"0" = root subject); the LEVEL is derived from depth-from-root, not the
// possibly-null `level` column. A category leaf carries `code = String(id)`,
// which is the topic_type_id that findTopicTypeId resolves for save_all.
export function adjacencyToTree(rows: FlatTopicRow[] | null | undefined): TaxonomySubject[] {
  if (!Array.isArray(rows)) return [];

  const childrenByParent = new Map<string, FlatTopicRow[]>();
  const roots: FlatTopicRow[] = [];
  rows.forEach((row) => {
    if (!row || row.id == null || String(row.id) === '') return;
    if (isRootParent(row.parent_id)) {
      roots.push(row);
    } else {
      const key = String(row.parent_id);
      const bucket = childrenByParent.get(key);
      if (bucket) bucket.push(row);
      else childrenByParent.set(key, [row]);
    }
  });

  const build = (
    row: FlatTopicRow,
    depth: number,
    parentId: string,
    seen: Set<string>
  ): TaxonomyNode => {
    const id = String(row.id);
    seen.add(id);
    const level = LEVEL_BY_DEPTH[Math.min(depth, LEVEL_BY_DEPTH.length - 1)];
    const name = String(row.topic || '').trim();
    const node = makeNode(
      level,
      depth === 0 ? normalizeSubjectName(name) : name,
      id, // code = backend PK; for a leaf this is the topic_type_id
      String(row.performance_indicator || ''),
      [],
      parentId
    );
    const kids = (childrenByParent.get(id) || [])
      .filter((kid) => !seen.has(String(kid.id))) // guard against cyclic data
      .sort(sortFlatRows);
    node.children = kids.map((kid) => build(kid, depth + 1, node.id, seen));
    return node;
  };

  return roots.sort(sortFlatRows).map((row) => {
    const subject = build(row, 0, '', new Set<string>()) as TaxonomySubject;
    subject.level = 'subject';
    return subject;
  });
}

// Resolve the Henry topic_type_id for a fully-selected cascade path. The
// category leaf carries it as `code` (adjacencyToTree stores String(row.id)
// there, and a leaf's id IS its topic_type_id). Returns undefined until the
// deepest level is picked. Used by the editor to set
// EditorialQuestionInput.topic_id, which save_all requires.
export function findTopicTypeId(
  subjects: TaxonomySubject[],
  path: { subject?: string; domain?: string; unit?: string; category?: string }
): number | undefined {
  if (!path.subject || !path.domain || !path.unit || !path.category) {
    return undefined;
  }
  const subject = subjects.find((s) => s.name === path.subject);
  const area = subject?.children.find((a) => a.name === path.domain);
  const unit = area?.children.find((u) => u.name === path.unit);
  const category = unit?.children.find((c) => c.name === path.category);
  const id = category ? Number(category.code) : NaN;
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

// One client surface shared by the Henry and mock implementations so the
// 分類架構 page can call granular CRUD regardless of backend. `replaceAll` is
// optional (mock-only bulk path); Henry uses per-node add/update/delete.
export interface TaxonomyClient {
  list(): Promise<TaxonomySubject[]>;
  addNode(
    level: TaxonomyLevel,
    input: { name: string; description?: string },
    parent: TaxonomyNode | null
  ): Promise<void>;
  updateNode(node: TaxonomyNode, input: { name: string; description?: string }): Promise<void>;
  deleteNode(node: TaxonomyNode): Promise<void>;
  replaceAll?(subjects: TaxonomySubject[]): Promise<TaxonomySubject[]>;
}

export const taxonomyHenryClient: TaxonomyClient = {
  async list(): Promise<TaxonomySubject[]> {
    const res = await mainResourceClient.getTopicTypeFlatList();
    // Tolerate both finalized {total, rows} and the older {count, list} shape.
    return adjacencyToTree(res.rows || res.list || []);
  },

  // node.code carries the backend topic_type id (set by adjacencyToTree);
  // parent.code is the new row's parent_id ('' for a root subject).
  async addNode(level, input, parent): Promise<void> {
    await mainResourceClient.topicTypeFlatAdd({
      topic: input.name,
      performance_indicator: input.description || undefined,
      parent_id: parent?.code || '',
      level: LEVEL_NUM[level],
      sort: 1
    });
  },

  async updateNode(node, input): Promise<void> {
    // Henry's update only applies non-empty fields, so an empty description
    // can't clear an existing indicator — acceptable for now.
    await mainResourceClient.topicTypeFlatUpdate({
      id: node.code,
      topic: input.name,
      performance_indicator: input.description ?? ''
    });
  },

  async deleteNode(node): Promise<void> {
    // Server blocks TOPIC_HAS_CHILDREN and TOPIC_HAS_QUESTIONS; the 分類架構
    // page also guards children client-side for faster UX and maps those codes
    // to notistack toasts.
    await mainResourceClient.topicTypeFlatDelete({ id: node.code });
  }
};

export const taxonomyClient: TaxonomyClient =
  process.env.REACT_APP_EDITORIAL_QUESTION_CLIENT === 'mock'
    ? taxonomyMockClient
    : taxonomyHenryClient;

// ---------------------------------------------------------------------------
// Subject-name cache for the sidebar's 題目列表 filter sub-nav.
//
// The sidebar renders on every dashboard page, so the subject list (the top
// keys of taxonomyToObjectiveCascade(list())) is cached module-wide instead of
// refetched per render. A plain cache would go stale when a subject is
// added/deleted in 分類架構, so the cache is invalidatable + reactive:
// invalidateSubjectOptions() (called at the 分類架構 CRUD sites) clears the
// cache, refetches, and notifies subscribers (the useSubjectOptions hook) so
// the sidebar updates live with no reload.
// ---------------------------------------------------------------------------

let subjectCache: string[] | null = null;
let subjectInflight: Promise<string[]> | null = null;
const subjectListeners = new Set<() => void>();

export function getCachedSubjectOptions(): string[] | null {
  return subjectCache;
}

export function loadSubjectOptions(force = false): Promise<string[]> {
  if (!force && subjectCache !== null) {
    return Promise.resolve(subjectCache);
  }
  if (!subjectInflight) {
    subjectInflight = taxonomyClient
      .list()
      .then((subjects) => {
        const next = Object.keys(taxonomyToObjectiveCascade(subjects));
        subjectCache = next;
        return next;
      })
      .catch(() => subjectCache ?? [])
      .finally(() => {
        subjectInflight = null;
      });
  }
  return subjectInflight;
}

export function seedSubjectOptions(subjects: TaxonomySubject[]): void {
  subjectCache = Object.keys(taxonomyToObjectiveCascade(subjects));
  subjectListeners.forEach((listener) => listener());
}

export function invalidateSubjectOptions(): void {
  subjectCache = null;
  loadSubjectOptions(true).then(() => {
    subjectListeners.forEach((listener) => listener());
  });
}

export function subscribeSubjectOptions(listener: () => void): () => void {
  subjectListeners.add(listener);
  return () => {
    subjectListeners.delete(listener);
  };
}
